/**
 * Open practical task — retell, answer in your own words, a real-life situation. With a
 * Gemini key the tutor checks the answer and returns a corrected version; without one the
 * learner compares with the keywords and marks the result honestly.
 */
import { useState } from 'react';
import type { Lesson } from '../../core/content.ts';
import { allSentences, isChinese, langCode } from '../../core/content.ts';
import type { OpenTask } from '../../core/lesson-plan.ts';
import { gradePrompt, parseGrade, usedKeywords, type Grade } from '../../core/ai-tasks.ts';
import { askJson, getGeminiKey } from '../../data/gemini.ts';
import { TUTOR_ERROR_TEXT, TutorError } from '../../core/tutor.ts';
import { RoboCat } from '../../ui/RoboCat.tsx';
import { Icon } from '../../ui/Icon.tsx';

type Phase =
  | { kind: 'writing' }
  | { kind: 'checking' }
  | { kind: 'graded'; grade: Grade; xp: number }
  | { kind: 'self'; note: string | null }
  | { kind: 'self-done'; ok: boolean; xp: number };

const MIN_LENGTH = 8;

export function OpenTaskStep({
  lesson,
  task,
  speechLang,
  onAnswered,
  onDone,
}: {
  lesson: Lesson;
  task: OpenTask;
  speechLang: string;
  onAnswered: (correct: boolean) => number;
  onDone: () => void;
}) {
  const [answer, setAnswer] = useState('');
  const [phase, setPhase] = useState<Phase>({ kind: 'writing' });
  const used = usedKeywords(answer, task.keywords);
  const zh = isChinese(lesson);

  const check = async () => {
    if (!getGeminiKey()) {
      setPhase({ kind: 'self', note: null });
      return;
    }
    setPhase({ kind: 'checking' });
    try {
      const context = allSentences(lesson)
        .map((s) => s.text)
        .join(' ')
        .slice(0, 1500);
      const raw = await askJson(gradePrompt(langCode(lesson.targetLang), lesson.title.text, task, answer, context));
      const grade = parseGrade(raw);
      if (!grade) throw new TutorError('unknown');
      setPhase({ kind: 'graded', grade, xp: onAnswered(grade.ok) });
    } catch (error) {
      const note = error instanceof TutorError ? TUTOR_ERROR_TEXT[error.code] : null;
      setPhase({ kind: 'self', note });
    }
  };

  const selfMark = (ok: boolean) => setPhase({ kind: 'self-done', ok, xp: onAnswered(ok) });
  const locked = phase.kind !== 'writing';

  return (
    <div className="stack stack--24 open-task">
      <p className="t-caps">{task.title}</p>
      <article className="card stack">
        <p className={task.promptLang === 'target' ? 'prompt han' : 'prompt prompt--ru'} lang={task.promptLang === 'target' ? speechLang : 'ru'}>
          {task.prompt}
        </p>
        {task.outline && (
          <ol className="open-task__outline han" lang={speechLang}>
            {task.outline.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ol>
        )}
        {task.keywords.length > 0 && (
          <div className="row open-task__keywords" aria-label="Слова для ответа">
            {task.keywords.map((k) => (
              <span key={k} className={`chip han${used.includes(k) ? ' chip--done' : ''}`} lang={speechLang}>
                {used.includes(k) && <Icon name="check" size={14} />}
                {k}
              </span>
            ))}
          </div>
        )}
      </article>

      <textarea
        className="input open-task__input"
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
        disabled={locked}
        rows={5}
        lang={speechLang}
        aria-label="Ваш ответ"
        placeholder={zh ? 'Пишите иероглифами (клавиатура с пиньинем)' : 'Write your answer'}
        autoCapitalize="sentences"
        spellCheck={!zh}
      />

      {phase.kind === 'checking' && (
        <div className="row open-task__status">
          <RoboCat size={48} state="thinking" />
          <p className="t-meta">Учитель читает ваш ответ…</p>
        </div>
      )}

      {phase.kind === 'graded' && (
        <div className={`feedback feedback--${phase.grade.ok ? 'correct' : 'wrong'} feedback--inline`} role="status">
          <div className="feedback__head">
            <strong className="feedback__title">{phase.grade.ok ? 'Хороший ответ!' : 'Можно лучше'}</strong>
            {phase.xp > 0 && <span className="feedback__xp">+{phase.xp} XP</span>}
          </div>
          <div className="feedback__body">
            <RoboCat size={56} state={phase.grade.ok ? 'happy' : 'thinking'} />
            <div className="feedback__bubble">
              <span className="tutor-label">Учитель</span>
              <p>{phase.grade.feedback}</p>
              {phase.grade.corrected && (
                <p className="han" lang={speechLang}>
                  <span className="t-meta">Как сказать лучше: </span>
                  {phase.grade.corrected}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {phase.kind === 'self' && (
        <div className="card stack open-task__self" role="status">
          {phase.note && <p className="t-meta">{phase.note}</p>}
          <p>
            Проверьте себя: ответ по теме и в нём есть слова урока ({used.length} из {task.keywords.length}).
            {!getGeminiKey() && ' С ключом Gemini в Профиле ответ проверит учитель.'}
          </p>
          <div className="row">
            <button type="button" className="btn btn--secondary" onClick={() => selfMark(false)}>
              Ещё не получилось
            </button>
            <button type="button" className="btn btn--primary" onClick={() => selfMark(true)}>
              Получилось
            </button>
          </div>
        </div>
      )}

      <div className="lesson__footer">
        {phase.kind === 'writing' ? (
          <>
            <button type="button" className="btn btn--text" onClick={onDone}>
              Пропустить
            </button>
            <button
              type="button"
              className="btn btn--ink"
              disabled={answer.trim().length < MIN_LENGTH}
              onClick={() => void check()}
            >
              Проверить
            </button>
          </>
        ) : (
          (phase.kind === 'graded' || phase.kind === 'self-done') && (
            <button type="button" className="btn btn--primary btn--block" onClick={onDone}>
              Продолжить
            </button>
          )
        )}
      </div>
    </div>
  );
}
